import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import CryptoJS from 'crypto-js';
import Icon from "./Icons";
import { useAuth } from "../AuthContext";
const base64 = require('js-base64').Base64;

const LocalApiUrl = "/api";

const Login = () => {
  const navigate = useNavigate();
  const { setIsLoggedIn } = useAuth();
  const [user,    setUser]    = useState({ email: "", password: "" });
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(false);

  const encryptUserId = (userId) => {
    const key = process.env.REACT_APP_SECRET_KEY || "Yq3t6v9y";
    const encrypted = CryptoJS.AES.encrypt(userId.toString(), key).toString();
    return base64.encode(encrypted, true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${LocalApiUrl}/user/login`, user);
      if (res.data?.userId) {
        const enc = encryptUserId(res.data.userId);
        localStorage.setItem("authToken",        res.data.token);
        localStorage.setItem("userId",           res.data.userId);
        localStorage.setItem("encryptedUserId",  enc);
        localStorage.setItem("firstName",        res.data.firstName);
        localStorage.setItem("lastName",         res.data.lastName);
        setIsLoggedIn(true);
        navigate("/addbook?refresh=true");
      } else {
        setError("Invalid response from server.");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Account not activated — check your email for the activation link.");
      } else if (err.response?.status === 400) {
        setError("Invalid email or password.");
      } else {
        setError("Unable to connect. Please check your internet connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
      <div className="hidden border-r border-ink-border bg-gradient-to-br from-[#17252e] via-ink-surface to-ink-base p-12 lg:flex lg:flex-col lg:justify-between">
        <Link to="/" className="inline-flex w-fit items-center gap-2 text-sm text-cream-muted no-underline transition-colors hover:text-cream"><Icon name="arrow" className="rotate-180" size={15} /> Back to StoryChats</Link>
        <div className="max-w-lg">
          <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-gold/10 text-gold"><Icon name="journal" size={22} /></span>
          <p className="font-serif text-4xl leading-tight tracking-[-0.025em] text-cream">“A reader lives a thousand lives before he dies.”</p>
          <p className="mt-5 text-sm text-cream-muted">— George R. R. Martin</p>
        </div>
        <p className="text-xs text-cream-muted">Your notes, progress, and conversations are waiting.</p>
      </div>

      <div className="flex items-center justify-center px-5 py-16 sm:px-10">
        <div className="w-full max-w-md">
          <div className="mb-9">
            <p className="sc-kicker mb-3">Welcome back</p>
            <h1 className="font-serif text-4xl font-semibold text-cream tracking-[-0.03em]">Return to your books.</h1>
            <p className="text-cream-muted text-sm mt-2">Sign in to continue your reading life.</p>
          </div>

          {error && (
            <div className="bg-crimson/10 border border-crimson/30 text-crimson-light rounded-xl px-4 py-3 text-sm mb-5">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="sc-panel flex flex-col gap-5 p-6 sm:p-7">
            <div className="flex flex-col gap-1.5">
              <label className="text-cream-dim text-xs font-medium">Email address</label>
              <input type="email" value={user.email} onChange={e => setUser({ ...user, email: e.target.value })} placeholder="you@example.com" className="sc-input" required />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label className="text-cream-dim text-xs font-medium">Password</label>
                <Link to="/forgot-password" className="text-xs text-gold no-underline transition-colors hover:text-gold-light">Forgot password?</Link>
              </div>
              <input type="password" value={user.password} onChange={e => setUser({ ...user, password: e.target.value })} placeholder="••••••••" className="sc-input" required />
            </div>

            <button type="submit" disabled={loading} className="sc-btn-gold py-3 mt-1 disabled:opacity-50 w-full">
              {loading ? "Signing in…" : <><span>Sign in</span><Icon name="arrow" size={15} /></>}
            </button>
          </form>

          <p className="text-center text-cream-muted text-sm mt-6">
            New here?{" "}<Link to="/register" className="text-gold no-underline hover:text-gold-light transition-colors">Create your library</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
